import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, Stack } from '@mui/material';
import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateRegistration } from '../../../registration/hooks';
import {
    ControlledDatePicker,
    DialogTitleWithClose,
    IRegistrationForm,
    RegistrationInputs,
    useSelectedOrganisation,
} from '../../../shared';
import { registrationFromFormMapper, registrationToFormMapper } from '../../../shared/mappers';
import { useRegistrationSchema } from '../../../registration/validators';
import { useGetSortedCustomAttributeConfigurations } from '../../../shared/utils/get-sorted-custom-attribute-configurations.util';
import { useCustomAttributeConfigurationList } from '../../../admin/hooks/custom-attributes-configuration.hook';

interface Props {
    show: boolean;
    supportCase: string;
    handleClose: () => void;
}

export const SaveRegistrationModal: FC<Props> = ({ show, supportCase, handleClose }) => {
    const { t } = useTranslation();
    const { organisation } = useSelectedOrganisation();

    const { mutateAsync: createRegistration, isPending: isCreatingRegistration } = useCreateRegistration();
    const { data: customAttributeConfigurations } = useCustomAttributeConfigurationList({
        organisationId: organisation?.id,
        pageSize: 100,
    });
    const sortedConfigurations = useGetSortedCustomAttributeConfigurations(customAttributeConfigurations?.data);

    const form = useForm<IRegistrationForm>({
        defaultValues: { dateOfRegistration: new Date(new Date().toDateString()) },
        resolver: yupResolver(useRegistrationSchema()),
        mode: 'all',
    });

    useEffect(() => {
        form.reset({
            ...registrationToFormMapper(organisation, sortedConfigurations),
            dateOfRegistration: new Date(new Date().toDateString()),
        });
    }, [form, organisation, show, sortedConfigurations]);

    const onSubmit = async (data: IRegistrationForm) => {
        if (supportCase && organisation && data.supportType.id && sortedConfigurations) {
            handleClose();
            await createRegistration({
                supportCase,
                registration: registrationFromFormMapper(data, organisation?.id, sortedConfigurations),
            });
        }
    };

    return (
        <Dialog open={show} onClose={handleClose} fullWidth maxWidth="sm">
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <DialogTitleWithClose onClose={handleClose}>{t('addRegistration')}</DialogTitleWithClose>
                    <DialogContent>
                        <Stack spacing={2}>
                            <ControlledDatePicker name={'dateOfRegistration'} label={t('date')} />
                            <RegistrationInputs customAttributeConfigurations={sortedConfigurations} />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" variant="contained" color="primary" disabled={isCreatingRegistration}>
                            {t('save')}
                        </Button>
                        <Button onClick={handleClose} color="secondary">
                            {t('cancel')}
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
